import { Box } from '@mentimeter/ragnar-ui/box';
import { useRef } from 'react';
import { scaleOrdinal } from 'd3-scale';
import type {
  ChoicesStaticOptions,
  Segment,
  SegmentedChoicesData,
  Visualization,
} from '../../types';
import { SegmentsLegend } from '../../bars/segmented-bars/SegmentsLegend';
import { useSegmentHover } from '../../bars/segmented-bars/useSegmentHover';
import { DotsGrid } from '../dots-grid';
import { Option } from '../option';
import { getLongestLabel } from '../../utils/get-longest-label';
import { LOW_OPACITY } from '../../bars/constants';
import { getScoreDisplayText } from '../score/getScoreDisplayText';
import { MAX_NUMBER_OF_DOTS_WITHOUT_LAG } from '../constants';

const SEGMENTATION_LEGEND_WIDTH_PERCENTAGE = 0.3;

interface SegmentedDotsProps
  extends Visualization<SegmentedChoicesData, ChoicesStaticOptions> {
  segments: Segment[];
  sourceQuestionTitle: string;
  shouldShowCorrect: boolean;
  shouldShowScorePercentage: false | { percentOf: number };
  segmentKeyAccessor: (d: Segment) => number;
  isScoreHidden: boolean;
}

export const SegmentedDots = ({
  colors,
  width,
  height,
  staticOptions,
  segments,
  sourceQuestionTitle,
  data,
  fontFamily,
  textColor,
  shouldShowCorrect,
  shouldShowScorePercentage,
  disableAnimations,
  valueAccessor = (d: SegmentedChoicesData) =>
    d.scores.reduce((acc, score) => acc + score, 0),
  isScoreHidden,
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  keyAccessor = (d: SegmentedChoicesData | ChoicesStaticOptions) =>
    d.id.toString(),
  segmentKeyAccessor = (d: Segment) => d.position,
}: SegmentedDotsProps) => {
  const { onSegmentHover, getIsSegmentHovered, isAnySegmentHovered } =
    useSegmentHover();
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const legendWidth = width * SEGMENTATION_LEGEND_WIDTH_PERCENTAGE;
  const visualizationWidth = Math.max(width - legendWidth, 0);

  const discreteSegmentValues = segments.map(segmentKeyAccessor);
  const segmentColors = scaleOrdinal(colors).domain(
    discreteSegmentValues.map(String),
  );

  if (width <= 0 || height <= 0) {
    return null;
  }

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  const largestOptionScore = Math.max(...data.map(valueAccessor), 0);

  return (
    <Box
      width={width}
      height={height}
      flexDirection="row"
      alignItems="center"
      data-testid="segmented-dots"
    >
      <DotsGrid
        width={visualizationWidth}
        height={height}
        ref={labelsContainerRef}
        style={{ opacity: 0 }}
        largestNumberOfDots={Math.min(
          largestOptionScore,
          MAX_NUMBER_OF_DOTS_WITHOUT_LAG,
        )}
        aria-hidden
      >
        {staticOptions.map((option) => {
          const optionId = keyAccessor(option);
          const isLongestLabel = longestLabelId === optionId;
          const label = labelAccessor(option);
          const optionData = data.find((d) => d.id === option.id);

          const numberOfSegments = optionData?.scores?.length ?? 1;
          const maxNumberOfDotsPerSegment = Math.round(
            MAX_NUMBER_OF_DOTS_WITHOUT_LAG / numberOfSegments,
          );

          const scores = optionData
            ? optionData.scores.map((score, i) => ({
                segmentId: `${optionId}-${i}`,
                score: Math.min(score, maxNumberOfDotsPerSegment),
                color: segmentColors(i.toString()),
                isFadedOut: isAnySegmentHovered && !getIsSegmentHovered(i),
              }))
            : 0;

          const totalScore = optionData ? valueAccessor(optionData) : 0;

          return (
            <Option
              key={optionId}
              width={0}
              height={0}
              label={label}
              color="black"
              scores={scores}
              scoreDisplayText={getScoreDisplayText(
                totalScore ?? 0,
                shouldShowScorePercentage,
              )}
              shouldRenderTextResizer={
                isLongestLabel ? { labelsContainerRef } : false
              }
              isCorrect={option.isMarkedCorrect}
              fontFamily={fontFamily}
              textColor={textColor}
              shouldShowCorrect={shouldShowCorrect}
              disableAnimations={disableAnimations}
            />
          );
        })}
      </DotsGrid>
      <SegmentsLegend
        segments={segments.map((segment) => ({
          ...segment,
          color: segmentColors(segmentKeyAccessor(segment).toString()),
          opacity:
            isAnySegmentHovered &&
            !getIsSegmentHovered(segmentKeyAccessor(segment))
              ? LOW_OPACITY
              : 1,
        }))}
        maxHeight={height}
        sourceQuestionTitle={sourceQuestionTitle}
        onSegmentHover={onSegmentHover}
        segmentKeyAccessor={segmentKeyAccessor}
      />
    </Box>
  );
};
